import { atom } from 'jotai';
import { auth } from '@/lib/firebase';


const baseUrl = process.env.NEXT_PUBLIC_CATALOG_API || '';
const mediaAccessApi = process.env.NEXT_PUBLIC_MEDIA_ACCESS_API || '';
const authApi = process.env.NEXT_PUBLIC_AUTH_API || 'http://3.15.235.104/auth';

export interface Artist {
    id: string;
    name: string;
    type: string;
    cover_image_url?: string;
    s3_url?: string;
}

export interface Song {
    id: string;
    title: string;
    artistId: string;
}

export const artistTypeOptions = [
    { value: "singer", label: "Singer" },
    { value: "band", label: "Band" },
    { value: "composer", label: "Composer" },
    { value: "writer", label: "Writer" },
];

interface ArtistListResponse {
    total_ct: number;
    data: Artist[];
}

export interface UserInfo {
    displayName: string;
    email: string;
    photoURL?: string;
    role?: string;
}

export const artistsAtom = atom<Artist[]>([]);
export const songsAtom = atom<Song[]>([]);
export const currentPageAtom = atom<string>('upload-artist');
export const loadingAtom = atom<boolean>(false);
export const currentArtistIdAtom = atom<string>('');
export const userAtom = atom<UserInfo | null>(null);
export const isSidebarCollapsedAtom = atom<boolean>(false);
export const isLoggingOutAtom = atom<boolean>(false);

export type AuthStatus = 'checking' | 'authenticated' | 'unauthenticated';
export const authStatusAtom = atom<AuthStatus>('checking');

export const getStoredToken = () => {
    if (typeof window === 'undefined') return '';
    return localStorage.getItem('authToken') || '';
};

export const setStoredToken = (token: string) => {
    if (typeof window !== 'undefined') {
        localStorage.setItem('authToken', token);
    }
};

export const removeStoredToken = () => {
    if (typeof window !== 'undefined') {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
    }
};

export const fetchWithAuth = async (url: string, options: RequestInit = {}) => {
    let token = getStoredToken();

    // Proactively refresh token if Firebase user is available
    if (auth.currentUser) {
        try {
            token = await auth.currentUser.getIdToken();
            setStoredToken(token);
        } catch (e) {
            console.error('Proactive token refresh failed:', e);
        }
    }

    const headers: Record<string, string> = {
        ...((options.headers as Record<string, string>) || {}),
    };

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    const res = await fetch(url, { ...options, headers });

    if (res.status === 401) {
        removeStoredToken();
        if (typeof window !== 'undefined') {
            window.location.href = '/login';
        }
    }

    return res;
};

export const syncUserWithBackend = async (token: string) => {
    const res = await fetch(`${authApi}/api/v1/auth/sync-user`, {
        method: 'GET',
        headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`
        }
    });

    if (!res.ok) {
        const errorText = await res.text();
        console.error('Sync user error:', res.status, errorText);
        throw new Error('Failed to authorize user with backend');
    }

    return res.json();
};

export const createArtist = async (
    name: string,
    type: string
): Promise<{
    artist: Artist;
    upload_url?: string;
    s3_key?: string;
}> => {
    const res = await fetchWithAuth(`${baseUrl}/api/v1/artist`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            name,
            type,
            cover_image_url: null,
        }),
    });

    if (!res.ok) throw new Error('Failed to create artist');

    const artist: Artist = await res.json();

    if (!artist?.id) {
        return { artist };
    }

    try {
        const { upload_url, s3_key } = await getCoverUploadUrl(artist.id);
        return { artist, upload_url, s3_key };
    } catch (err) {
        console.error('Presigned URL fetch failed:', err);
        return { artist };
    }
};

export const getCoverUploadUrl = async (
    artistId: string
): Promise<{ upload_url: string; s3_key: string }> => {
    const res = await fetchWithAuth(
        `${mediaAccessApi}/api/v1/artist/cover-image/upload?artist_id=${artistId}`,
        {
            method: 'GET',
            headers: {
                Accept: 'application/json',
            },
        }
    );

    if (!res.ok) {
        const text = await res.text();
        console.error('Upload URL error:', res.status, text);
        throw new Error('Failed to get upload URL');
    }

    return res.json();
};

export const getCoverDownloadUrl = async (
    artistId: string
): Promise<string> => {
    const res = await fetchWithAuth(
        `${mediaAccessApi}/api/v1/artist/cover-image/download?artist_id=${artistId}`,
        {
            method: 'GET',
            headers: {
                Accept: 'application/json',
            },
        }
    );

    if (!res.ok) {
        console.error('Download URL error:', await res.text());
        return '';
    }

    const data = await res.json();
    return data.download_url || '';
};

export const fetchArtists = async (
    pageNo = 1,
    limit = 10
): Promise<Artist[]> => {
    const res = await fetchWithAuth(
        `${baseUrl}/api/v1/artist?page_no=${pageNo}&limit=${limit}`,
        {
            method: 'GET',
            headers: {
                Accept: 'application/json',
            },
        }
    );

    if (!res.ok) {
        console.error('Fetch artists error:', await res.text());
        throw new Error('Failed to fetch artists');
    }

    const data: ArtistListResponse = await res.json();
    return data.data || [];
};

export const getArtistById = async (id: string): Promise<Artist> => {
    const res = await fetchWithAuth(`${baseUrl}/api/v1/artist/${id}`, {
        method: 'GET',
        headers: {
            Accept: 'application/json',
        },
    });
    if (!res.ok) throw new Error('Failed to get artist');
    return res.json();
};

export const updateArtistDetails = async (
    id: string,
    name: string,
    type: string,
    cover_image_url?: string
): Promise<Artist> => {
    const body: Record<string, string> = { name, type };
    if (cover_image_url) {
        body.cover_image_url = cover_image_url;
    }
    const res = await fetchWithAuth(`${baseUrl}/api/v1/artist/${id}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error('Failed to update artist details');
    return res.json();
};

export const deleteArtist = async (id: string): Promise<void> => {
    const res = await fetchWithAuth(`${baseUrl}/api/v1/artist/${id}`, {
        method: 'DELETE',
    });
    if (!res.ok) throw new Error('Failed to delete artist');
};

export const uploadFileToS3 = async (
    uploadUrl: string,
    file: File
): Promise<void> => {
    const res = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
            'Content-Type': 'image/jpeg',
        },
        body: file,
    });

    if (!res.ok) {
        const text = await res.text();
        console.error('S3 upload error:', text);
        throw new Error('Failed to upload file');
    }
};
